import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Image as ImageIcon, Video, X, Check } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MediaSelector({ open, onOpenChange, onSelect, type = 'image' }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (open) {
      fetchMedia();
    }
  }, [open]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/media`, { headers });
      setMedia(res.data.filter(m => type === 'all' || m.file_type === type));
    } catch (err) {
      console.error('Error fetching media:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredMedia = media.filter(m => 
    m.filename.toLowerCase().includes(search.toLowerCase()) ||
    m.category.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = () => {
    if (selected) {
      // Devolvemos la URL tal cual está en la base de datos (relativa o absoluta)
      onSelect(selected.url);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col p-0 overflow-hidden bg-white">
        <DialogHeader className="p-6 pb-0 border-b">
          <DialogTitle className="text-2xl font-serif">Biblioteca de Medios</DialogTitle>
          <div className="flex gap-4 items-center mt-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar archivos..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex">
          <ScrollArea className="flex-1 p-6">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="aspect-square bg-slate-100 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filteredMedia.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {filteredMedia.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelected(item)}
                    className={`group relative aspect-square rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                      selected?.id === item.id ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-slate-200'
                    }`}
                  >
                    {item.file_type === 'image' ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50">
                        <Video className="w-8 h-8 text-primary/40" />
                        <span className="text-[10px] mt-1 text-slate-500 uppercase font-bold">Video</span>
                      </div>
                    )}
                    
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    
                    {selected?.id === item.id && (
                      <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1 border-2 border-white shadow-lg">
                        <Check className="w-3 h-3" />
                      </div>
                    )}
                    
                    <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-black/60 translate-y-full group-hover:translate-y-0 transition-transform">
                      <p className="text-[10px] text-white truncate">{item.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground py-20">
                <ImageIcon className="w-12 h-12 mb-4 opacity-20" />
                <p>No se encontraron archivos</p>
              </div>
            )}
          </ScrollArea>
        </div>

        <div className="p-4 border-t bg-slate-50 flex justify-between items-center px-6">
          <div className="text-sm text-slate-500">
            {selected ? (
              <span className="flex items-center gap-2">
                Seleccionado: <strong className="text-slate-900">{selected.filename}</strong>
              </span>
            ) : (
              'Ningún archivo seleccionado'
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button disabled={!selected} onClick={handleSelect}>Seleccionar Archivo</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
